import {get} from 'object-path-immutable'
import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {fullCardDefinition} from '../../../common/card-assembler/samples'
import themeAssembler from '../../../common/theme-assembler'
import {CardPreview} from '../../../components/card-preview/preview'
import {ElapsedAbsoluteTime} from '../../../components/formatters'
import {IconDropdown} from '../../../components/library/dropdown/dropdown'
import {NoResultsPermissions} from '../../../components/library/list'
import styled from 'styled-components'
import {Tag} from '../../../components/library/tag/tag'
import {Loader} from '../../../components/loader'
import {useThemeListQuery} from '../../../data/workbench-api/generated/theme'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useGetEnvironmentCardActions} from '../../../hooks/use-get-environment-card-actions'
import {getTestDataMemo} from '../../../selectors/card-template'
import {Overflow} from './overflow'
import {useSdkVersionSelector} from '../../../hooks/use-sdk-version-selector'

const ThemeCards = styled.div`
  padding: 30px;
`

const ThemeCard = styled.div`
  background: ${p => p.theme.white};

  &:focus,
  &:hover {
    background: ${p => p.theme.white50};
  }
  &:active {
    background: ${p => p.theme.white35};
  }

  @media (min-width: 800px) {
    padding-right: 60px;
  }

  border-radius: ${p => p.theme.borderRadius};
  margin-bottom: 30px;
  position: relative;
  cursor: pointer;

  .theme {
    height: 300px;
    padding: 30px;
    outline: none;
    display: grid;
    grid-gap: 30px;
    grid-template-columns: auto;
    grid-template-rows: 1fr 1fr;

    @media (min-width: 800px) {
      grid-template-rows: auto;
      grid-template-columns: 1fr 1fr;
    }

    width: 100%;
    border: none;
    overflow: hidden;
    justify-content: space-between;
  }
`

const ThemeLeft = styled.div`
  display: flex;
  flex-direction: column;
`

const ThemeRight = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  height: 270px;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    box-shadow: inset 0 -1px 0 0 ${p => p.theme.grey05};
    z-index: 30;
  }

  > div > div {
    display: block;
    min-width: 0;
    padding: 0;
  }
`

const DropdownPositioner = styled.div`
  position: absolute;
  top: 30px;
  right: 30px;
`

const ThemeTitle = styled.div`
  color: ${p => p.theme.blue};
  font-size: ${p => p.theme.fontSizeL};
  line-height: ${p => p.theme.lineHeightL};
`
const ThemeMeta = styled.div`
  margin-top: 10px;
  color: ${p => p.theme.grey50};
  font-size: ${p => p.theme.fontSizeS};
  line-height: ${p => p.theme.lineHeightS};
`

export const List = ({environment}: {environment: EnvironmentNode}) => {
  const canView = environment.hasPermission(
    PermissionResource.Theme,
    PermissionAction.View
  )

  const {data, refetch, loading} = useThemeListQuery({
    variables: {
      environmentId: environment.id
    }
  })

  const [subviewId, setSubviewId] = useState<string>('')

  const themes = data?.themes?.nodes ?? []

  const {environmentCardActions, loading: actionsLoading} =
    useGetEnvironmentCardActions(environment.id, environment.organisationId)

  // TODO remove later (this is just temporary to remove old unsaved themes)
  useEffect(() => {
    new Promise<void>(res => {
      Object.keys(localStorage)
        .filter(k => k.startsWith('tempTheme_'))
        .forEach(k => {
          localStorage.removeItem(k)
        })
      res()
    })
  }, [])

  const {currentSdkVersion} = useSdkVersionSelector(environment.organisationId)

  if (loading || actionsLoading) {
    return (
      <ThemeCards>
        <Loader />
      </ThemeCards>
    )
  }

  return (
    <ThemeCards>
      {canView ? (
        themes.map((theme, i) => (
          <ThemeCard key={`theme-${theme.id}`}>
            <Link className={'theme'} key={i} to={`../../themes/${theme.id}`}>
              <ThemeLeft>
                <ThemeTitle>{theme.name} </ThemeTitle>

                {theme.isDefault && (
                  <ThemeMeta>
                    <Tag appearance="light">Default</Tag>
                  </ThemeMeta>
                )}
                <ThemeMeta>
                  {`Edited `}
                  <ElapsedAbsoluteTime lowerCase={true} date={theme.updated} />
                </ThemeMeta>
              </ThemeLeft>
              <ThemeRight>
                <CardPreview
                  sdkVersion={currentSdkVersion}
                  cardDefinition={fullCardDefinition}
                  variables={get(
                    fullCardDefinition,
                    'definition.variables',
                    {}
                  )}
                  eventDetail={getTestDataMemo(fullCardDefinition, false)}
                  subviewId={subviewId}
                  setSubviewId={sId => setSubviewId(sId ?? '')}
                  getAssetUrl={() => ''}
                  theme={themeAssembler(theme.payload).assembledTheme}
                  environmentCardActions={environmentCardActions}
                />
              </ThemeRight>
            </Link>
            <DropdownPositioner>
              <IconDropdown icon="overflow" size="small" style={{zIndex: 31}}>
                {closeDropdown => (
                  <Overflow
                    theme={theme}
                    environment={environment}
                    editLink={`../../themes/${theme.id}`}
                    closeDropdown={closeDropdown}
                    refetch={refetch}
                    canEdit={environment.hasPermission(
                      PermissionResource.Theme,
                      PermissionAction.Edit
                    )}
                  />
                )}
              </IconDropdown>
            </DropdownPositioner>
          </ThemeCard>
        ))
      ) : (
        <NoResultsPermissions />
      )}
    </ThemeCards>
  )
}
